import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';
import { TabWorkspaceService } from './tab-workspace.service';
import { RuntimeService } from '../shared/services/runtime.service';

/**
 * Cached route handles from TabRouteReuseStrategy must be destroyed when a tab
 * closes. Deleting the Map entry alone leaves the component tree alive, so:
 * - document listeners (e.g. drag mouseout) stay registered
 * - Angular CDK DropList IDs remain in the global `_dropLists` registry
 *
 * Manual Load Planning drop-list IDs are `leg_${flightLegId}_${zoneId}`. A second
 * Plan Now for the same flight registers new lists with the same IDs; CDK's
 * `find(list => list.id === id)` returns the stale detached lists first, so
 * drag still moves the preview but enter/placeholder/drop stop working —
 * especially visible in the Electron EXE (no full page reload like web home).
 */
@Injectable()
export class TabRouteReuseStrategy implements RouteReuseStrategy {
  private readonly storedHandles = new Map<string, DetachedRouteHandle>();

  constructor(
    private readonly workspace: TabWorkspaceService,
    private readonly runtime: RuntimeService
  ) {}

  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    // Web has no tab bar (see layout.component.html) — only one implicit tab
    // exists there, so tab-scoped caching just holds every visited route's
    // component alive forever for no benefit. Fall back to default Angular
    // behavior (destroy on navigate away) on web; keep caching desktop-only.
    return !this.runtime.isWeb && this.workspace.isReusableRoute(route);
  }

  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    if (!this.workspace.isReusableRoute(route)) {
      return;
    }

    if (!handle) {
      // Angular calls store(route, null) right after a successful retrieve()/reattach,
      // as a signal that the cached entry is now live again — clear our reference to it
      // without destroying the view, using the same tab id retrieve() used to find it.
      const key = this.workspace.buildRouteCacheKey(route, this.workspace.getRetrieveTabId());
      this.storedHandles.delete(key);
      return;
    }

    const key = this.workspace.buildRouteCacheKey(route, this.workspace.getStoreTabId());
    const previous = this.storedHandles.get(key);
    if (previous && previous !== handle) {
      this.destroyHandle(previous);
    }
    this.storedHandles.set(key, handle);
  }

  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    if (this.runtime.isWeb || !this.workspace.isReusableRoute(route)) {
      return false;
    }

    const key = this.workspace.buildRouteCacheKey(route, this.workspace.getRetrieveTabId());
    return this.storedHandles.has(key);
  }

  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    if (!this.workspace.isReusableRoute(route)) {
      return null;
    }

    const key = this.workspace.buildRouteCacheKey(route, this.workspace.getRetrieveTabId());
    return this.storedHandles.get(key) ?? null;
  }

  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    if (future.routeConfig !== curr.routeConfig) {
      return false;
    }

    return this.workspace.getStoreTabId() === this.workspace.getRetrieveTabId();
  }

  clearTab(tabId: string): void {
    for (const key of Array.from(this.storedHandles.keys())) {
      if (key.startsWith(`${tabId}::`)) {
        const handle = this.storedHandles.get(key);
        this.storedHandles.delete(key);
        if (handle) {
          this.destroyHandle(handle);
        }
      }
    }
  }

  clearAll(): void {
    for (const handle of this.storedHandles.values()) {
      this.destroyHandle(handle);
    }
    this.storedHandles.clear();
  }

  private destroyHandle(handle: DetachedRouteHandle): void {
    const componentRef = (handle as { componentRef?: { destroy: () => void } })?.componentRef;
    try {
      componentRef?.destroy();
    } catch {
      // Ignore double-destroy / already-torn-down trees.
    }
  }
}
